import React, { useMemo, useState } from "react";
import { PORTFOLIO, FILTERS } from "@/lib/portfolio";
import { ArrowUpRight } from "lucide-react";
import { whatsappWith, openExternal } from "@/lib/contact";
import Reveal from "@/components/Reveal";

export default function Portfolio() {
  const [active, setActive] = useState("all");

  const items = useMemo(
    () =>
      active === "all"
        ? PORTFOLIO
        : PORTFOLIO.filter((p) => p.category === active),
    [active]
  );

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative py-24 sm:py-32 border-t border-white/5 bg-[#0C0C0E]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
          <Reveal>
            <span className="label-overline">/ 03 — Selected work</span>
            <h2 className="font-display text-4xl sm:text-5xl mt-6 leading-[1.05] font-semibold tracking-tight max-w-xl">
              Handcrafted prints from the studio floor.
            </h2>
          </Reveal>
          <div className="flex flex-wrap gap-1.5" data-testid="portfolio-filters">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={`portfolio-filter-${f.id}`}
                onClick={() => setActive(f.id)}
                className={`px-4 h-10 text-xs font-mono uppercase tracking-[0.18em] border transition-colors ${
                  active === f.id
                    ? "border-[#FF5722] text-[#FF5722] bg-[#FF5722]/5"
                    : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Masonry — CSS columns; each card sizes to its natural image aspect */}
        <div
          className="portfolio-masonry columns-1 sm:columns-2 lg:columns-3 gap-4 sm:gap-5"
          style={{ columnFill: "balance" }}
        >
          {items.map((it, idx) => (
            <PortfolioCard key={it.id} item={it} idx={idx} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PortfolioCard({ item, idx }) {
  return (
    <div className="mb-4 sm:mb-5 break-inside-avoid">
      <Reveal delay={Math.min(idx, 8) * 60}>
        <a
          data-testid={`portfolio-item-${item.id}`}
          href={whatsappWith(
            `Hi 3D PRINT HUB, I'd like a quote inspired by your "${item.title}".`
          )}
          target="_blank"
          rel="noreferrer"
          onClick={(e) =>
            openExternal(
              e,
              whatsappWith(
                `Hi 3D PRINT HUB, I'd like a quote inspired by your "${item.title}".`
              )
            )
          }
          aria-label={`Inquire about ${item.title}`}
          className="portfolio-card group relative block overflow-hidden bg-[#0F0F11] border border-white/8 hover:border-[#FF5722]/50"
        >
          {/* Natural-aspect image — no crop, no letterbox */}
          <div className="relative overflow-hidden">
            <img
              src={item.img}
              alt={item.title}
              loading="lazy"
              className="portfolio-img block w-full h-auto"
            />

            {/* Dark gradient at bottom for caption legibility on hover */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/85 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

            {/* Tag badge */}
            <span className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.2em] uppercase bg-black/70 backdrop-blur-sm px-2.5 py-1 border border-white/15 text-zinc-100 transition-colors duration-500 group-hover:border-[#FF5722]/70 group-hover:text-[#FF5722]">
              {item.tag}
            </span>

            {/* Arrow badge */}
            <span className="absolute top-3 right-3 w-9 h-9 grid place-items-center border border-white/15 bg-black/60 backdrop-blur-sm text-zinc-200 transition-all duration-500 group-hover:border-[#FF5722] group-hover:bg-[#FF5722] group-hover:text-white group-hover:rotate-45">
              <ArrowUpRight size={14} />
            </span>

            {/* Description-only overlay slides in on hover (title stays in the caption strip below to avoid duplication) */}
            <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
              <p className="text-zinc-100 text-xs sm:text-sm leading-relaxed max-w-[42ch]">
                {item.desc}
              </p>
            </div>
          </div>

          {/* Persistent caption strip below — always visible so nothing is hidden without hover */}
          <div className="flex items-center justify-between gap-3 border-t border-white/5 px-4 py-3">
            <h3 className="font-display text-sm sm:text-base leading-tight text-white line-clamp-1">
              {item.title}
            </h3>
            <span className="shrink-0 font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-500 group-hover:text-[#FF5722] transition-colors">
              #{String(idx + 1).padStart(2, "0")}
            </span>
          </div>
        </a>
      </Reveal>
    </div>
  );
}
